import {
  first,
  filter,
  flatten,
  get,
  reject,
  join,
  find,
  size,
  without,
  toString,
  toNumber,
  isArray,
  uniqBy
} from 'lodash'
import dayjs from 'dayjs'
import { useI18n } from '@ggs/i18n'
import defaultSectionComponent from '../../mock_data/DefaultSectionComponent.json'

/**
 * @typedef {import('../../definitions').StackTableData} StackTableData
 * @typedef {import('../../definitions').StackTableRow} StackTableRow
 */

/**
 * @typedef {import('../../definitions').CourseSectionExpRecord} CourseSectionExpRecord
 * @typedef {import('../../definitions').CourseSectionRecord} CourseSectionRecord
 * @typedef {import('../../definitions').SectionComponent} SectionComponent
 * @typedef {import('../../definitions').CourseRecord} CourseRecord
 * @typedef {import('../../definitions').InstructorList} InstructorList
 * @typedef {import('../../definitions').SubjectCourseList} SubjectCourseList
 * @typedef {import('../../definitions').CourseList} CourseList
 * @typedef {import('../../definitions').SubjectList} SubjectList
 * @typedef {import('../../definitions').ProgramStudyList} ProgramStudyList
 * @typedef {import('../../definitions').CourseSubjectRecord} CourseSubjectRecord
 * @typedef {import('../../definitions').StandardTimetableRecord} StandardTimetableRecord
 * @typedef {import('../../definitions').CourseSectionTimetable} CourseSectionsTimetable
 * @typedef {import('../../definitions').CourseSectionsTimetablesList} CourseSectionsTimetablesList
 * @typedef {import('../../definitions').CourseSectionMeetingDate} CourseSectionMeetingDate
 */

/**
 * @typedef {(courseSections: CourseSectionExpRecord[]) => InstructorList} DigestInstructorsList
 */

/**
 * @typedef {Object} CourseSectionInstructor
 * @property {string} fullName
 * @property {string} employeeId
 */

/**
 * @typedef {(coursesData: CourseSectionExpRecord[]) => SubjectCourseList} DigestSubjectCourseList
 * @typedef {(coursesData: CourseRecord[]) => CourseList} DigestCoursesList
 * @typedef {(subjectsData: CourseSubjectRecord[]) => SubjectList} DigestSubjectsList
 * @typedef {(timetables: StandardTimetableRecord[]) => ProgramStudyList} DigestProgramStudyList
 * @typedef {(courseSections: CourseSectionExpRecord[], standardTimetable: StandardTimetableRecord) => CourseSectionsTimetablesList} DigestCourseSectionsTimetables
 * @typedef {(courseSection: CourseSectionExpRecord) => StackTableRow} ComposeCourseSectionTable
 * @typedef {(courseSection: CourseSectionExpRecord) => StackTableData} ComposeScheduleTable
 * @typedef {(courseSection: CourseSectionExpRecord) => CourseSectionInstructor[]} GetCourseSectionInstructors
 * @typedef {(items: string[]) => JSX.Element|string} ComposeItemsList
 */

/**
 * @typedef {Object} AcademicCoursesDataDigestHook
 * @property {DigestSubjectCourseList} digestSubjectCoursesList
 * @property {DigestCoursesList} digestCoursesList
 * @property {DigestSubjectsList} digestSubjectsList
 * @property {ComposeCourseSectionTable} composeSectionComponentsTableData
 * @property {ComposeCourseSectionTable} composeSectionDetailTableData
 * @property {ComposeScheduleTable} composeSectionScheduleTableData
 * @property {GetCourseSectionInstructors} getCourseSectionInstructors
 * @property {DigestProgramStudyList} digestProgramStudyList
 * @property {ComposeItemsList} composeItemsList
 * @property {DigestInstructorsList} digestInstructorsList
 * @property {DigestCourseSectionsTimetables} digestCourseSectionsTimetable
 */

/**
 * Hook that process academic data digestion.
 * @return {AcademicCoursesDataDigestHook}
 */
const useAcademicDataDigest = () => {
  const { t } = useI18n()
  const inPersonMode = ['In-Person', 'Hybrid', 'Multi-access']

  /**
   * Pick subject course attributes needed for course sections table.
   * @type {DigestSubjectCourseList}
   * @param {CourseSectionExpRecord[]} courseSection
   * @return {SubjectCourseList}
   */
  const digestSubjectCoursesList = (courseSection) => {
    // Use a Map to filter duplicates and maintain insertion order for sorting
    const courseMap = new Map()

    // Map the course sections to the required structure and fill the map
    courseSection.forEach((item) => {
      const courseCode = `${item?.course?.courseSubject?.code} ${item?.course?.courseNumber}`
      if (!courseMap.has(courseCode)) {
        // Check if the courseCode has already been added
        const courseNumber = item.course?.courseNumber || ''
        courseMap.set(courseCode, {
          courseId: item?.course?.courseId || '',
          courseInstanceId: item?.course?.courseInstanceId || '',
          courseCode,
          courseTitle: item?.course?.title || item?.course?.abbreviatedTitle || '',
          subjectCode: item?.course?.courseSubject?.code || '',
          sectionNumber: item?.sectionNumber || '',
          courseLevel: courseNumber.charAt(0) || '',
        })
      }
    })

    // Convert the Map values to an array and sort it
    return Array.from(courseMap.values()).sort((a, b) => {
      return a.courseCode.localeCompare(b.courseCode, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    })
  }

  /**
   * Pick course object attributes needed for subject course table.
   * @type {DigestCoursesList}
   * @param {CourseRecord[]} coursesData
   * @return {CourseList}
   */
  const digestCoursesList = (coursesData) => {
    return coursesData.map((item) => {
      const subjectDescription = item.courseSubject?.description || ''
      // Remove campus name from subject description to produce the title.
      const subjectTitle = subjectDescription.replace(/\((\w)\)/, '')
      const courseCode = item?.courseCode || ''
      const specialTopics = without(item.specialTopics, 'null', 'Null')
      const specialTopicsString = join(specialTopics, ', ') || ''
      const courseNumber = item.courseNumber || ''
      return {
        courseId: item?.courseId || '',
        courseCode: `${courseCode} ${specialTopicsString}`,
        courseTitle: item?.title || item?.abbreviatedTitle || '',
        subjectCode: item?.courseSubject?.code || '',
        subjectTitle,
        courseLevel: courseNumber.charAt(0) || '',
      }
    })
  }

  /**
   * Expand a date range rule into a list of dates.
   *
   * @param {string} startDate
   * @param {string} endDate
   * @param {string} startTime
   * @param {string} endTime
   * @param {number[]} weekDays Zero-based index of allowed days of week.
   * @return {CourseSectionMeetingDate[]}
   */
  function expandDateRangePattern(startDate, endDate, startTime, endTime, weekDays) {
    const dates = []
    const daysOfWeek = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ]
    const currentDate = new Date(startDate)
    const endTimeDate = new Date(`1970-01-01T${endTime}`)
    const startTimeDate = new Date(`1970-01-01T${startTime}`)

    while (currentDate <= new Date(endDate)) {
      const dayOfWeek = daysOfWeek[currentDate.getDay()]
      if (weekDays.includes(daysOfWeek.indexOf(dayOfWeek))) {
        const startDate = new Date(currentDate)
        startDate.setHours(startTimeDate.getHours(), startTimeDate.getMinutes(), 0, 0)
        const endDate = new Date(currentDate)
        endDate.setHours(endTimeDate.getHours(), endTimeDate.getMinutes(), 0, 0)
        dates.push({ startDate, endDate })
      }

      currentDate.setDate(currentDate.getDate() + 1)
    }

    return dates
  }

  /**
   * Pick course section object attributes needed for standard timetable table.
   * @type {DigestCourseSectionsTimetables}
   * @param {CourseSectionExpRecord[]} courseSections
   * @param {StandardTimetableRecord} standardTimetable
   * @return {CourseSectionsTimetablesList}
   */
  const digestCourseSectionsTimetable = (courseSections, standardTimetable) => {

    /** @type {Map<string, CourseSectionsTimetable>} */
    const timetable = new Map()


    courseSections.forEach((item) => {
      const code = getFormattedSectionCode(item)

      /**
       * Compose course section meeting dates array.
       * @param {SectionComponent} sectionComponent
       * @return {CourseSectionMeetingDate[]}
       */
      const composeMeetingDates = (sectionComponent) => {
        const meetingDaysOfWeek = sectionComponent?.meetingDayPattern?.daysOfTheWeek
        const startDate = sectionComponent?.startDate
        const endDate = sectionComponent?.endDate
        const startTime = sectionComponent?.startTime
        const endTime = sectionComponent?.endTime

        if (!meetingDaysOfWeek || !startDate || !endDate || !startTime || !endTime) {
          return null
        }

        // Decrease week day index by 1 to match the index of the daysOfWeek array, which is zero based.
        const weekDays = meetingDaysOfWeek.map((day) => {
          const dayIndex = toNumber(day?.dayOfTheWeekId) || 0
          return dayIndex - 1
        })

        if (!isArray(weekDays) || size(weekDays) <= 0) {
          return null
        }

        return expandDateRangePattern(startDate, endDate, startTime, endTime, weekDays)
      }

      const sectionComponents = item?.sectionComponents || []
      const meetings = sectionComponents.map((sectionComponent) =>
        composeMeetingDates(sectionComponent)
      )

      const courseSectionTimetable = {
        courseSectionId: item.id || '',
        timetableId: standardTimetable.id || '',
        name: standardTimetable.name || '',
        description: standardTimetable.description || '',
        year: standardTimetable.classStanding?.code || '',
        meetings: flatten(meetings).map((meeting) => ({ title: code, ...meeting })) || [],
      }

      if (timetable.has(courseSectionTimetable.name)) {
        const existingTimetable = timetable.get(courseSectionTimetable.name)
        existingTimetable.meetings = [...existingTimetable.meetings, ...courseSectionTimetable.meetings]
        timetable.set(courseSectionTimetable.name, existingTimetable)
      } else {
        timetable.set(courseSectionTimetable.name, courseSectionTimetable)
      }
    })

    console.log('timetable', timetable)
    return Array.from(timetable, ([_, value]) => (value))
  }

  /**
   * Compose items HTML list.
   * @type {ComposeItemsList}
   * @param {string[]} items
   * @return {JSX.Element|string}
   */
  const composeItemsList = (items) => {
    if (size(items) <= 0) {
      return ''
    }

    const listItems = items.map((item, index) => {
      return <li key={index}>{item}</li>
    })

    return <ul>{listItems}</ul>
  }

  /**
   * Pick program study object attributes needed for programs table.
   * @type {DigestProgramStudyList}
   * @param {StandardTimetableRecord[]} timetables
   * @return {ProgramStudyList}
   */
  const digestProgramStudyList = (timetables) => {
    return uniqBy(timetables.map((item) => {
      return {
        id: item.id || '',
        programStudyTitle: item.programArea || t('Unknown Name'),
        year: item.classStanding?.code || '',
      }
    }), 'programStudyTitle')
  }

  /**
   * Pick subject object attributes needed for subjects table.
   * @type {DigestSubjectsList}
   * @param {CourseSubjectRecord[]} subjectsData
   * @return {SubjectList}
   */
  const digestSubjectsList = (subjectsData) => {
    return subjectsData.map((item) => {
      return {
        subjectId: item.id || '',
        subjectCode: item.code || '',
        subjectTitle: item.name || '',
        academicUnitId: reject(item.academicUnitIds, (academicUnitId) => {
          // Filter academic units that represent campus level.
          return academicUnitId.includes('ACADEMIC_UNIT-UBC-')
        }),
        academicUnitName: '',
      }
    })
  }

  /**
   * Produce formatted subject code for a course section.
   * @param {CourseSectionExpRecord} courseSection
   * @return {string}
   */
  const getFormattedSectionCode = (courseSection) => {
    // Compose section code.
    const subjectCode = courseSection.course?.courseSubject?.code || 'N/A'
    const sectionNumber = courseSection.sectionNumber || 'N/A'
    const courseNumber = toString(courseSection.course?.courseNumber) || 'N/A'
    const separatorRegex = new RegExp(/(^[-_]+|[-_]+$)/g)
    const sectionParts = [
      courseNumber.replace(separatorRegex, ''),
      sectionNumber.replace(separatorRegex, ''),
    ]

    return `${subjectCode} ${join(sectionParts, '-')}`
  }

  /**
   * @typedef {Object} CourseSectionSchedule
   * @property {string[]} dayLabels
   * @property {string} dayLabelsString
   * @property {string} startTime
   * @property {string} endTime
   * @property {string} startDate
   * @property {string} endDate
   */

  /**
   * Produce formatted subject code dates schedule.
   * @param {CourseSectionExpRecord} courseSection
   * @return {CourseSectionSchedule[]}
   */
  const getFormattedSchedule = (courseSection) => {
    const sectionComponents = courseSection.sectionComponents ?? [defaultSectionComponent]

    return sectionComponents.map((sectionComponent) => {
      // Compose day names string.
      const daysOfWeek = sectionComponent.meetingDayPattern?.daysOfTheWeek ?? []
      const alternativeWeeks = sectionComponent?.meetingDayPattern?.recurrentWeekNumber
      const dayLabels = daysOfWeek.map(
        (/** @type {{ descriptor: string; }} */ dayOfWeek) => dayOfWeek.descriptor
      )
      const dayLabelsString =
        join(filter(dayLabels), ', ') + (alternativeWeeks === 2 ? ' (Alternate weeks)' : '')
      // Compose start - end datetime.
      const startDate = `${sectionComponent.startDate || courseSection.startDate} ${
        sectionComponent.startTime || 'NA'
      }`
      const endDate = `${sectionComponent.endDate || courseSection.endDate} ${
        sectionComponent.endTime || 'NA'
      }`
      const startDateTime = dayjs(startDate, 'YYYY-MM-DD HH:mm:ss')
      const endDateTime = dayjs(endDate, 'YYYY-MM-DD HH:mm:ss')

      return {
        dayLabels,
        dayLabelsString,
        startTime: startDateTime.isValid() ? startDateTime.format('HH:mm') : '',
        endTime: endDateTime.isValid() ? endDateTime.format('HH:mm') : '',
        startDate: startDateTime.isValid() ? startDateTime.format('YYYY-MM-DD') : '',
        endDate: endDateTime.isValid() ? endDateTime.format('YYYY-MM-DD') : '',
      }
    })
  }

  /**
   * Compose course section component.
   * @type {ComposeCourseSectionTable}
   * @param {CourseSectionExpRecord} courseSection
   * @return {StackTableRow}
   */
  const composeSectionComponentsTableData = (courseSection) => {
    const sectionCode = getFormattedSectionCode(courseSection)
    const schedule = first(getFormattedSchedule(courseSection))
    const deliverMode = courseSection.courseComponent?.deliveryMode?.code || ''
    const inPersonAttendanceRequired = inPersonMode.includes(deliverMode) ? t('Yes') : t('No')

    // Extract course sections data.
    return [
      {
        field: 'section',
        label: 'Section',
        value: sectionCode,
      },
      {
        field: 'deliveryMode',
        label: 'Delivery Mode',
        value: courseSection.courseComponent?.deliveryMode?.description || '',
      },
      {
        field: 'status',
        label: 'Status',
        value: courseSection.sectionStatus?.description || '',
      },
      {
        field: 'term',
        label: 'Term',
        value: courseSection.academicPeriod?.academicPeriodType || '',
      },
      {
        field: 'activity',
        label: 'Activity',
        value: courseSection.courseComponent.instructionalFormat.description,
      },
      {
        field: 'credits',
        label: 'Credits',
        value: `${courseSection.creditUnits}`,
      },
      { field: 'startDate', label: 'Start Date', value: courseSection.startDate || '' },
      {
        field: 'startTime',
        label: 'Start Time',
        value: schedule.startTime || '',
      },
      { field: 'days', label: 'Days', value: schedule.dayLabelsString },
      { field: 'endDate', label: 'End Date', value: courseSection.endDate || '' },
      { field: 'endTime', label: 'End Time', value: schedule.endTime || '' },
      {
        field: 'sectionComments',
        label: 'Section Comments',
        value: 'View Comments',
        modalContent: courseSection.publicNotes || '',
      },
    ]
  }

  /**
   * Compose section detail data.
   * @type {ComposeCourseSectionTable}
   * @param {CourseSectionExpRecord} courseSection
   * @return {StackTableRow}
   */
  const composeSectionDetailTableData = (courseSection) => {
    const deliverMode = courseSection.courseComponent?.deliveryMode?.code || ''
    const inPersonAttendanceRequired = inPersonMode.includes(deliverMode) ? t('Yes') : t('No')

    return [
      {
        field: 'credits',
        label: 'Credits',
        value: `${courseSection.creditUnits}`,
      },
      {
        field: 'startDate',
        label: 'Start Date',
        value: dayjs(courseSection.startDate).format('MMM DD, YYYY') || '',
      },
      {
        field: 'deliveryMode',
        label: 'Delivery Mode',
        value: courseSection.courseComponent?.deliveryMode?.description || '',
      },
      {
        field: 'endDate',
        label: 'End Date',
        value: dayjs(courseSection.endDate).format('MMM DD, YYYY') || '',
      },
      {
        field: 'term',
        label: 'Term',
        value: courseSection.academicPeriod?.academicPeriodType || '',
      },
    ]
  }

  /**
   * Compose section schedule data.
   * @type {ComposeScheduleTable}
   * @param {CourseSectionExpRecord} courseSection
   * @return {StackTableData}
   */
  const composeSectionScheduleTableData = (courseSection) => {
    const sectionComponent = first(courseSection.sectionComponents) || defaultSectionComponent
    const schedules = getFormattedSchedule(courseSection)

    const tableRows = schedules
      .filter((schedule) => schedule.startDate && schedule.endDate)
      .sort((a, b) =>
        a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0)
      .map((schedule) => {
        return [
          {
            field: 'term',
            label: 'Term',
            value: courseSection.academicPeriod?.academicPeriodType || '',
          },
          {
            field: 'startDate',
            label: 'Start Date',
            value: dayjs(schedule.startDate).format('MMM DD, YYYY') || '',
          },
          {
            field: 'startTime',
            label: 'Start Time',
            value: schedule.startTime || '',
          },
          { field: 'days', label: 'Days', value: schedule.dayLabelsString },
          {
            field: 'endDate',
            label: 'End Date',
            value: dayjs(schedule.endDate).format('MMM DD, YYYY') || '',
          },
          { field: 'endTime', label: 'End Time', value: schedule.endTime || '' },
        ]
      })

    return tableRows
  }

  /**
   * Compose section schedule data.
   * @type {GetCourseSectionInstructors}
   * @param {CourseSectionExpRecord} courseSection
   * @return {CourseSectionInstructor[]}
   */
  const getCourseSectionInstructors = (courseSection) => {
    let teachingAssignments = courseSection.teachingAssignments

    if (!teachingAssignments) {
      return null
    }
    const publishedRoles = courseSection.publishedAssignableRoles || []
    if (publishedRoles && publishedRoles.length > 0) {
      teachingAssignments = teachingAssignments.filter((teachingAssignment) => {
        return publishedRoles.some((role) => role.code === teachingAssignment.assignableRole.code)
      })
    }
    /**
     * @type {CourseSectionInstructor[]}
     */
    const instructorData = []
    teachingAssignments.forEach(
      // @ts-ignore
        (teachingAssignment) => {
          const validIdentifier = teachingAssignment.identifiers.find(
            (identifier) => identifier.worker && identifier.worker.isActive
            
          )
          if (validIdentifier && validIdentifier.worker) {
            const employeeName =
              validIdentifier.worker.personNames.find((name) => name.nameType === 'Preferred Name') ||
              validIdentifier.worker.personNames.find((name) => name.nameType === 'Legal Name') ||
              validIdentifier.worker.personNames[0]
              const matchingRole = publishedRoles.some((role) => {
                return role.code === teachingAssignment.assignableRole.code
              })       
            matchingRole && instructorData.push({
              fullName: `${employeeName.givenName} ${employeeName.familyName}`,
              employeeId: validIdentifier.identifier || '',
            })
          }
      }
    )

    return instructorData
  }

  /**
   * Transform worker records into instructors data table list.
   * @type {DigestInstructorsList}
   * @param {CourseSectionExpRecord[]} courseSections
   * @return {InstructorList}
   */
  const digestInstructorsList = (courseSections) => {
    const courseSectionsInstructor = courseSections.map((item) => {
      const subjectCodeRaw = item.course?.courseSubject?.code || ''
      const subjectCode = subjectCodeRaw.replace(/_.*/, '')
      const teachingAssignments = item.teachingAssignments

      const emptyInstructor = {
        employeeUUID: '',
        employeeId: '',
        name: t('Unknown Teaching Assignment'),
        taughtSubject: subjectCode,
      }

      if (!isArray(teachingAssignments)) {
        return emptyInstructor
      }

      const instructor = first(teachingAssignments)
      const instructorEmployee = find(instructor?.identifiers, { identifierType: 'Employee_ID' })
      if (!instructorEmployee) {
        return emptyInstructor
      }

      const personNames = get(instructorEmployee, 'worker.personNames', null)
      const selectedName =
        find(personNames, { nameType: 'Preferred Name' }) ||
        find(personNames, { nameType: 'Legal Name' }) ||
        first(personNames)

      return {
        employeeUUID: instructorEmployee?.identifier || '',
        employeeId: instructorEmployee?.identifier || '',
        name: selectedName ? `${selectedName.givenName} ${selectedName.familyName}` : '',
        taughtSubject: subjectCode,
      }
    })

    return filter(courseSectionsInstructor)
  }

  return {
    composeItemsList,
    composeSectionComponentsTableData,
    composeSectionDetailTableData,
    composeSectionScheduleTableData,
    digestCourseSectionsTimetable,
    digestCoursesList,
    digestInstructorsList,
    digestProgramStudyList,
    digestSubjectCoursesList,
    digestSubjectsList,
    getCourseSectionInstructors,
  }
}

export default useAcademicDataDigest
